/**
 * Remember-me credential store.
 *
 * Persists the login email + password across app restarts so returning
 * users don't have to retype them. Works uniformly across the three
 * runtimes we ship on:
 *
 *   - Web browsers → localStorage (origin-scoped, survives tab close)
 *   - Capacitor iOS (WKWebView) → localStorage IS persistent per Capacitor's
 *     WebView config (data survives app kill / reboot)
 *   - Capacitor Android (WebView) → same
 *
 * No native plugin required, so this works over-the-air the moment the
 * JS bundle updates — no `cap sync` + App Store / Play resubmission.
 *
 * The password is stored base64-encoded (NOT encrypted — that's not the
 * threat model here; the device lock screen is). Base64 just prevents
 * casual "open DevTools, read the password" over-the-shoulder attacks.
 */

const KEY_ENABLED = "calofay_remember_enabled";
const KEY_EMAIL   = "calofay_remembered_email";
const KEY_PWD     = "calofay_remembered_password";

function _encode(s) {
  try { return typeof btoa === "function" ? btoa(unescape(encodeURIComponent(s))) : s; }
  catch { return s; }
}
function _decode(s) {
  try { return typeof atob === "function" ? decodeURIComponent(escape(atob(s))) : s; }
  catch { return ""; }
}

export function loadRememberedCreds() {
  try {
    if (localStorage.getItem(KEY_ENABLED) !== "1") return null;
    const email = localStorage.getItem(KEY_EMAIL) || "";
    const encoded = localStorage.getItem(KEY_PWD) || "";
    const password = encoded ? _decode(encoded) : "";
    if (!email) return null;
    return { email, password };
  } catch {
    return null;
  }
}

export function saveRememberedCreds(email, password) {
  try {
    localStorage.setItem(KEY_ENABLED, "1");
    localStorage.setItem(KEY_EMAIL, email);
    localStorage.setItem(KEY_PWD, _encode(password));
  } catch { /* quota exceeded / private mode — silently ignore */ }
}

export function clearRememberedCreds() {
  try {
    localStorage.removeItem(KEY_ENABLED);
    localStorage.removeItem(KEY_EMAIL);
    localStorage.removeItem(KEY_PWD);
  } catch { /* ignore */ }
}

/** True if the user previously chose "Remember me". Used to
 * default-check the box on the login form. */
export function isRememberEnabled() {
  try { return localStorage.getItem(KEY_ENABLED) === "1"; }
  catch { return false; }
}
